/**
 * Vaishnavi Estate — Green Coffee Landing Page
 * Lightweight vanilla JS. No jQuery, no external libraries.
 * Every effect here degrades to "static but fully usable" when
 * JavaScript or IntersectionObserver is unavailable, and respects
 * prefers-reduced-motion throughout.
 */
(function () {
  'use strict';

  var reduceMotion = window.matchMedia('(prefers-reduced-motion: reduce)').matches;

  function init() {

  /* ---------------------------------------------------------------------
     1. Scroll reveal (+ optional blur-in) via IntersectionObserver
     ------------------------------------------------------------------- */
  var revealEls = document.querySelectorAll('.gc-reveal, .gc-blur-in');
  if ('IntersectionObserver' in window && revealEls.length) {
    var io = new IntersectionObserver(
      function (entries) {
        entries.forEach(function (entry) {
          if (entry.isIntersecting) {
            entry.target.classList.add('gc-in');
            io.unobserve(entry.target);
          }
        });
      },
      { threshold: 0.15, rootMargin: '0px 0px -60px 0px' }
    );
    revealEls.forEach(function (el) { io.observe(el); });
  } else {
    revealEls.forEach(function (el) { el.classList.add('gc-in'); });
  }

  /* ---------------------------------------------------------------------
     2. Hero: subtle scroll parallax + mouse-move drift
     ------------------------------------------------------------------- */
  if (!reduceMotion) {
    document.querySelectorAll('[data-gc-hero]').forEach(function (hero) {
      var media = hero.querySelector('[data-gc-parallax-media]');
      var card = hero.querySelector('[data-gc-parallax-card]');
      var ticking = false;

      function applyScrollParallax() {
        ticking = false;
        var rect = hero.getBoundingClientRect();
        if (rect.bottom < 0 || rect.top > window.innerHeight) return;
        var progress = (window.innerHeight - rect.top) / (window.innerHeight + rect.height);
        var shift = (progress - 0.5) * 40; // max ~20px either way
        if (media) media.style.transform = 'translate3d(0,' + shift.toFixed(1) + 'px,0) scale(1.06)';
      }
      window.addEventListener('scroll', function () {
        if (!ticking) { window.requestAnimationFrame(applyScrollParallax); ticking = true; }
      }, { passive: true });
      applyScrollParallax();

      if (card && window.matchMedia('(pointer: fine)').matches) {
        hero.addEventListener('mousemove', function (e) {
          var rect = hero.getBoundingClientRect();
          var relX = (e.clientX - rect.left) / rect.width - 0.5;
          var relY = (e.clientY - rect.top) / rect.height - 0.5;
          card.style.transform = 'translate3d(' + (relX * -8).toFixed(1) + 'px,' + (relY * -6).toFixed(1) + 'px,0)';
        });
        hero.addEventListener('mouseleave', function () {
          card.style.transform = 'translate3d(0,0,0)';
        });
      }
    });
  }

  /* ---------------------------------------------------------------------
     3. Smooth scroll for in-page CTAs
     ------------------------------------------------------------------- */
  document.addEventListener('click', function (e) {
    var trigger = e.target.closest('[data-gc-scroll-to]');
    if (!trigger) return;
    var target = document.querySelector(trigger.getAttribute('data-gc-scroll-to'));
    if (!target) return;
    e.preventDefault();
    target.scrollIntoView({ behavior: reduceMotion ? 'auto' : 'smooth', block: 'start' });
    window.setTimeout(function () {
      target.setAttribute('tabindex', '-1');
      target.focus({ preventScroll: true });
    }, reduceMotion ? 0 : 500);
  });

  /* ---------------------------------------------------------------------
     4. Product cards: variant-aware price + Add to cart / Quick buy
     ------------------------------------------------------------------- */
  function formatMoney(cents, format) {
    var value = (cents / 100).toFixed(2);
    return (format || '₹{{amount}}').replace('{{amount}}', value);
  }

  document.querySelectorAll('[data-gc-product-form]').forEach(function (form) {
    var variantsJSON = form.querySelector('[data-gc-variants]');
    var variants = [];
    try { variants = JSON.parse(variantsJSON.textContent); } catch (err) { variants = []; }

    var select = form.querySelector('[data-gc-variant-select]');
    var priceEl = form.querySelector('[data-gc-price]');
    var addBtn = form.querySelector('[data-gc-add-btn]');
    var statusEl = form.querySelector('[data-gc-status]');
    var idInput = form.querySelector('[data-gc-variant-id]');
    var moneyFormat = form.getAttribute('data-money-format') || '₹{{amount}}';

    function currentVariant() {
      var id = idInput.value;
      for (var i = 0; i < variants.length; i++) {
        if (String(variants[i].id) === String(id)) return variants[i];
      }
      return variants[0];
    }

    function updateForVariant(variant) {
      if (!variant) return;
      idInput.value = variant.id;
      if (priceEl) {
        var html = formatMoney(variant.price, moneyFormat);
        if (variant.compare_at_price && variant.compare_at_price > variant.price) {
          html = '<s>' + formatMoney(variant.compare_at_price, moneyFormat) + '</s> ' + html;
        }
        priceEl.innerHTML = html;
      }
      if (addBtn) {
        if (variant.available) {
          addBtn.disabled = false;
          addBtn.textContent = addBtn.getAttribute('data-label-available') || 'Add to Cart';
        } else {
          addBtn.disabled = true;
          addBtn.textContent = addBtn.getAttribute('data-label-sold-out') || 'Sold Out';
        }
      }
    }

    if (select) {
      select.addEventListener('change', function () {
        var variant = variants.filter(function (v) { return String(v.id) === select.value; })[0];
        updateForVariant(variant);
      });
    }
    updateForVariant(currentVariant());

    form.addEventListener('submit', function (e) {
      e.preventDefault();
      if (addBtn) addBtn.disabled = true;
      if (statusEl) { statusEl.textContent = 'Adding…'; statusEl.removeAttribute('data-state'); }

      fetch(form.getAttribute('action'), {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
        body: JSON.stringify({ id: idInput.value, quantity: 1 })
      })
        .then(function (res) { return res.json().then(function (data) { return { ok: res.ok, data: data }; }); })
        .then(function (result) {
          if (!result.ok) throw new Error(result.data && result.data.description ? result.data.description : 'Could not add to cart');
          if (statusEl) { statusEl.textContent = 'Added to cart.'; }
          document.dispatchEvent(new CustomEvent('gc:cart:add', { detail: result.data }));
        })
        .catch(function (err) {
          if (statusEl) { statusEl.textContent = err.message || 'Something went wrong.'; statusEl.setAttribute('data-state', 'error'); }
        })
        .finally(function () {
          var variant = currentVariant();
          if (addBtn) addBtn.disabled = !(variant && variant.available);
        });
    });
  });

  } // end init()

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }
})();
